import styled from 'styled-components';
import { palette } from 'styled-tools';

import { FontSizes, FontWeights, minWidth, Text } from '@calm-web/design-system';

export const Wrapper = styled.div`
	color: ${palette('white')};
`;

// 20px matches both platforms' uniform header rhythm (iOS: a single `spacing: 20` VStack
// covering title→narrator→button→description; Android: AssociationView's own 20dp
// pack_vertical_space top margin, same value reused for the button pack after it).
// Carries the 20px and the 480px cap that used to sit on Title, so the heart is a sibling of
// the heading inside the same column rather than something Title has to make room for.
// The heart is pinned to the far end, matching Android (button_fave is constrained to the
// parent's end, with the title constrained up to it) and lining up vertically with the
// narrator row's own trailing chevron below. iOS instead sets it immediately after the title
// text in an HStack, which on web's much wider column would leave it floating mid-row.
export const TitleRow = styled.div`
	align-items: center;
	display: flex;
	gap: 12px;
	justify-content: space-between;
	margin-bottom: 20px;
	max-width: 480px;
`;

// Takes the row hearts' -14px pull rather than Android's own header inset of +4dp, so this
// heart, the narrator row's chevron and every heart in the list share one trailing line.
// Android places each of the three from its own container with its own literal inset, landing
// their centres 28dp, 17dp and 26dp from the screen edge; the two hearts are a near-miss, the
// chevron is not close. The line chosen here is the row fave's 26dp, since that is the element
// the page repeats — web's column edge sits 16px in, so this -14px pull reproduces it exactly.
export const FaveHeartContainer = styled.div`
	flex-shrink: 0;
	margin-inline-end: -14px;
	width: 48px;
`;

// One size at every width, matching iOS, whose title1 is a flat 28pt with no size-class
// variance and no line limit — this renders at 27px. The tablet-and-up jump to 4xl that used to
// sit here was harmless while the heading had the whole page to run into, but the column is now
// capped at 480px, so a ~50px title wrapped to two lines where mobile keeps it on one.
export const Title = styled(Text).attrs({
	size: FontSizes.xl,
	weight: FontWeights.Medium,
	noMargin: true,
	el: 'h1',
})`
	text-align: left;
`;

// Everything on this page is a phone-proportioned column — the row width, thumbnail size, type
// scale and vertical rhythm are all matched to iOS and Android, which have no desktop
// counterpart to widen against. Rather than leave that column hugging the left edge of a wide
// viewport, it sits centred from tablet up, which is where the page has slack to centre within.
// The upsell banner stays outside this so it keeps spanning the full width as it does elsewhere.
export const ContentColumn = styled.div`
	${minWidth('tablet')} {
		margin-inline: auto;
		max-width: 480px;
	}
`;

// The program's own description, shown only on the legacy fallback branch. The packs feed
// carries this copy as a `paragraph` pack, so ParagraphPack renders it there and a second copy
// here would duplicate it; a program with no feed has no other source for it. Matches that
// pack's own treatment — plain body text, 16px below — so the two branches read the same.
export const Description = styled(Text).attrs({
	el: 'p',
	color: 'white',
	noMargin: true,
})`
	margin-bottom: 16px;
`;

// Caps the legacy fallback list to the same 480px column the header and the packs list use.
// GuideList and GuideRow both center themselves with `margin: 0 auto`, which lined up with
// this page's old page-wide `text-align: center`; now that the header is left-aligned to match
// mobile, that auto-centering left the guides adrift in the middle of a wide viewport. Capping
// the column removes the slack the auto margins were centering within, rather than editing
// those shared components, which AltProgramLayout also renders.
export const GuideListWrapper = styled.div`
	max-width: 480px;
`;

// Narrator (NarratorRow) doesn't set its own trailing margin — it's shared with the
// playlist's curator row, which manages its own spacing via PlaylistHeaderWrapper's gap.
// This 20px lives here instead, since it's specific to the program page's header rhythm.
export const NarratorWrapper = styled.div`
	margin-bottom: 20px;
`;
